import { computed, observable, runInAction } from 'mobx';
import CookieApi from '../util/CookieApi';
import { FetchStatus } from '../util/FetchStatus';
import { safeReaction } from '../util/MobxUtil';
import { pathnameWithoutLang } from '../util/NavigationUtil';
import LanguageStore from './LanguageStore';
import RootStore from './RootStore';
import SegmentStore from './SegmentStore';

export interface MenuItem {
  id: string;
  order?: number;
  parent?: number;
  label?: string;
  postId?: number;
  url?: string;
  path?: string;
  xfn?: string;
  classes?: string[];
  icon?: string;
  childMenuId?: number;
  children?: MenuItem[];
}

export interface Post {
  postId: number;
  label: string;
}

export interface Menu {
  items?: MenuItem[];
  postsByPath?: Map<string, Post>;
  status?: FetchStatus;
}

export const SEGMENT_COOKIE_NAME = 'segment';

class MenuStore {
  @observable accessor initialCookieSegment: string;
  @observable accessor enMenu: Menu;
  @observable accessor initialMenu: Menu;

  private languageStore: LanguageStore;
  private segmentStore: SegmentStore;

  constructor(rootStore: RootStore) {
    this.segmentStore = rootStore.segmentStore;
    this.languageStore = rootStore.languageStore;
    this.init(rootStore.initialState);

    safeReaction(
      rootStore,
      () => this.segmentStore.currentSegmentWithoutDefault + this.languageStore.currentLanguage,
      () => this.forgetInitialMenu()
    );
  }

  public init = (initialState: InitialState) => {
    runInAction(() => {
      this.enMenu = initialState.englishMenu;
      this.initialCookieSegment = initialState.customSegment;
      if (!this.languageStore.initialLanguageChanged) {
        this.initialMenu = initialState.menu;
      }
    });
  };

  public forgetInitialMenu = () => {
    runInAction(() => {
      this.initialMenu = null;
      this.initialCookieSegment = null;
    });
  };

  @computed
  get menuItems() {
    return this.initialMenu?.items;
  }

  public getCustomMenuSegment() {
    return this.initialCookieSegment || CookieApi.getCookieValue(SEGMENT_COOKIE_NAME);
  }

  @computed
  get enMenuPaths() {
    const menu = this.enMenu;
    if (menu && menu.postsByPath) {
      return Object.keys(menu.postsByPath)
        .filter((path) => path.startsWith('/'))
        .map(pathnameWithoutLang);
    }
    return [];
  }
}

export default MenuStore;
